// DOSSIER ZONE — the research document on a Person or Organisation page (BUILD-BRIEF-R2 §9).
//
// A dossier is a DOCUMENT, not a fact. It was written by an agent, nobody holds it, and nobody
// ever will: it carries ◇ for ever, and no verb on this zone turns one of its sentences into a
// fact — the facts the same run proposed sit in Review with their own quotes. What this zone
// does is make the receipts visible: every `[n]` in the prose is a control that unfolds the
// verbatim quote it rests on (`blockquote.ev`) with the snapshot's URL and its `dd.mm.yyyy`
// fetch date (`.ev-src`). Prose is `.read`; a quote is serif; a date is a figure; a URL or a
// ref is mono. Nothing here is decorated with a claim it did not make.
//
// When there is no dossier the zone says so in words. No empty sheet, no zero, no spinner
// pretending to be content.
import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { Att, Ev, Fig, Lnk, Ref, Sep, Sheet, Zone, ZoneHead } from './PageKit.js';
import { usePoll } from './usePoll.js';
import { useLocale, useT } from '../i18n/index.js';
import { getDossier, latestDossier, listDossiers, type DossierView } from '../api/dossiers.js';
import type { Citation, SubjectKind } from '../../shared/types.js';
import './DossierZone.de.js';
import './DossierZone.fr.js';

export interface DossierZoneProps {
  subjectKind: SubjectKind;
  subjectId: number;
}

const POLL_MS = 60_000;

// ── the body: a tiny, safe markdown reading ─────────────────────────────────

type Block =
  | { kind: 'heading'; text: string }
  | { kind: 'para'; text: string }
  | { kind: 'list'; items: string[] };

/** `##` headings, `-` lists, paragraphs. Anything else is a paragraph — never raw HTML. */
export function parseBody(body: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: string[] = [];
  const flush = () => {
    if (para.length) { blocks.push({ kind: 'para', text: para.join(' ') }); para = []; }
    if (list.length) { blocks.push({ kind: 'list', items: list }); list = []; }
  };
  for (const raw of body.split('\n')) {
    const line = raw.trimEnd();
    if (!line.trim()) { flush(); continue; }
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    if (heading) { flush(); blocks.push({ kind: 'heading', text: heading[1].trim() }); continue; }
    const item = /^\s*[-*]\s+(.*)$/.exec(line);
    if (item) { if (para.length) flush(); list.push(item[1].trim()); continue; }
    if (list.length) flush();
    para.push(line.trim());
  }
  flush();
  return blocks;
}

/** Split "text [1][2] more" into text runs and citation numbers. */
export function tokenise(text: string): Array<{ text: string } | { n: number }> {
  const out: Array<{ text: string } | { n: number }> = [];
  const re = /\[(\d+)\]/g;
  let last = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) out.push({ text: text.slice(last, m.index) });
    out.push({ n: Number(m[1]) });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

function CitedText({ text, citations, open, toggle }: {
  text: string;
  citations: Map<number, Citation>;
  open: Set<number>;
  toggle: (n: number) => void;
}) {
  const t = useT();
  const { fmtDate } = useLocale();
  const tokens = tokenise(text);
  const shown = tokens.filter((tok): tok is { n: number } => 'n' in tok).map((tok) => tok.n).filter((n) => open.has(n) && citations.has(n));
  return (
    <>
      <p className="read" style={{ margin: '0 0 8px' }}>
        {tokens.map((tok, i) => ('text' in tok
          ? <Fragment key={i}>{tok.text}</Fragment>
          : citations.has(tok.n)
            ? (
              <button
                key={i}
                type="button"
                className="lnk"
                aria-expanded={open.has(tok.n)}
                title={t('show the quote behind [{n}]', { n: tok.n })}
                onClick={() => toggle(tok.n)}
              >
                [{tok.n}]
              </button>
            )
            : <span key={i} className="n-none" title={t('this citation is not in the list')}>[{tok.n}]</span>
        ))}
      </p>
      {[...new Set(shown)].map((n) => {
        const c = citations.get(n)!;
        return (
          <Ev
            key={n}
            src={<>
              <Att kind="quoted" /> [{n}]
              <Sep />
              <a className="lnk" href={c.url} target="_blank" rel="noreferrer noopener"><Ref>{c.url}</Ref></a>
              <Sep />
              {c.fetched_at ? <Fig>{fmtDate(c.fetched_at)}</Fig> : <span className="fv--unknown">{t('fetch date not recorded')}</span>}
              <Sep />
              <Ref>#{c.snapshot_id}</Ref>
            </>}
          >
            {c.quote}
          </Ev>
        );
      })}
    </>
  );
}

// ── the zone ────────────────────────────────────────────────────────────────

export default function DossierZone({ subjectKind, subjectId }: DossierZoneProps) {
  const t = useT();
  const { fmtDate, tp } = useLocale();

  const fetcher = useCallback(() => latestDossier(subjectKind, subjectId), [subjectKind, subjectId]);
  const poll = usePoll<DossierView | null>(fetcher, POLL_MS);

  const [versions, setVersions] = useState<DossierView[] | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [viewing, setViewing] = useState<DossierView | null>(null);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [sourcesOpen, setSourcesOpen] = useState(false);

  // A new subject is a new document: forget what was unfolded for the last one.
  useEffect(() => { setViewing(null); setVersions(null); setVersionsOpen(false); setOpen(new Set()); setSourcesOpen(false); }, [subjectKind, subjectId]);

  const dossier = viewing ?? poll.data ?? null;
  const citations = useMemo(() => new Map((dossier?.citations ?? []).map((c) => [c.n, c])), [dossier]);
  const blocks = useMemo(() => parseBody(dossier?.body_md ?? ''), [dossier]);

  const toggle = useCallback((n: number) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n); else next.add(n);
      return next;
    });
  }, []);

  const showVersions = async () => {
    if (versionsOpen) { setVersionsOpen(false); return; }
    setVersionsOpen(true);
    if (!versions) {
      try {
        const res = await listDossiers(subjectKind, subjectId);
        setVersions(res.items);
      } catch {
        setVersions([]);
      }
    }
  };

  const view = async (id: number) => {
    try {
      setViewing(await getDossier(id));
      setOpen(new Set());
    } catch {
      /* the list keeps what it has; the poll's latest stays on screen */
    }
  };

  const count = dossier
    ? <><Fig>v{dossier.version}</Fig>{dossier.version_count && dossier.version_count > 1 ? <> <Sep /> <Fig>{tp(dossier.version_count, '{n} version', '{n} versions')}</Fig></> : null}</>
    : undefined;

  return (
    <Zone id="dossier">
      <ZoneHead
        label={t('Dossier')}
        count={count}
        note={dossier
          ? <><Att kind="proposed" title={t('written by an agent; a dossier is never held')} /> {t('written by')} <Ref>{dossier.run_ref ?? dossier.principal ?? '—'}</Ref> <Sep /> <Fig>{fmtDate(dossier.created_at)}</Fig></>
          : t('a document with its receipts, never a fact')}
        route={dossier && dossier.version_count && dossier.version_count > 1
          ? <button type="button" className="zone-route" onClick={() => { void showVersions(); }}>{versionsOpen ? t('Hide versions') : t('Previous versions')} →</button>
          : undefined}
      />

      {poll.error && !dossier ? (
        <Sheet pad><p className="zone-empty">{t('The dossier could not be read: {error}', { error: poll.error })}</p></Sheet>
      ) : poll.loading && !dossier ? (
        <Sheet pad><p className="zone-empty">{t('Loading…')}</p></Sheet>
      ) : !dossier ? (
        <Sheet pad>
          <p className="zone-empty">
            {t('No dossier yet. Research runs nightly for tier A, weekly for tier B, or now. A dossier is a document, not a fact: it keeps its ◇ mark for ever, and no verb turns one of its sentences into a fact. Each [n] expands to the verbatim quote and the snapshot it was read from.')}
          </p>
        </Sheet>
      ) : (
        <Sheet pad>
          {viewing && poll.data && viewing.id !== poll.data.id && (
            <p className="rat" style={{ marginBottom: 8 }}>
              {t('Showing version {v}; the latest is version {latest}.', { v: viewing.version, latest: poll.data.version })}{' '}
              <Lnk onClick={() => { setViewing(null); setOpen(new Set()); }}>{t('Back to the latest')}</Lnk>
            </p>
          )}
          <div className="move-head">
            <h2>{dossier.title ?? t('Untitled dossier')} <Ref className="d">{dossier.ref}</Ref></h2>
          </div>
          <div style={{ marginTop: 8 }}>
            {blocks.map((b, i) => {
              if (b.kind === 'heading') return <div key={i} className="fgroup-h u-label" style={{ marginTop: i ? 12 : 0 }}>{b.text}</div>;
              if (b.kind === 'list') {
                return (
                  <ul key={i} className="read" style={{ margin: '0 0 8px', paddingLeft: 18 }}>
                    {b.items.map((item, j) => <li key={j}><CitedText text={item} citations={citations} open={open} toggle={toggle} /></li>)}
                  </ul>
                );
              }
              return <CitedText key={i} text={b.text} citations={citations} open={open} toggle={toggle} />;
            })}
          </div>

          <div className="chips">
            <Lnk onClick={() => setSourcesOpen((v) => !v)}>
              {sourcesOpen ? t('Hide sources') : tp(dossier.citations.length, '{n} source', '{n} sources')}
            </Lnk>
          </div>
          {sourcesOpen && (
            <div style={{ marginTop: 8 }}>
              {dossier.citations.map((c) => (
                <div key={c.n} className="fact">
                  <span className="fk">[{c.n}]</span>
                  <span>
                    <a className="lnk" href={c.url} target="_blank" rel="noreferrer noopener"><Ref>{c.url}</Ref></a>
                    <blockquote className="ev">{c.quote}</blockquote>
                  </span>
                  <span className="fmeta">
                    {c.fetched_at ? <Fig>{fmtDate(c.fetched_at)}</Fig> : <span className="fv--unknown">{t('fetch date not recorded')}</span>}
                    {' '}<Ref>#{c.snapshot_id}</Ref>
                  </span>
                </div>
              ))}
            </div>
          )}

          {versionsOpen && (
            <div style={{ marginTop: 12 }}>
              <div className="fgroup-h u-label">{t('Versions')}</div>
              {versions === null ? (
                <p className="zone-empty">{t('Loading…')}</p>
              ) : versions.length === 0 ? (
                <p className="zone-empty">{t('The versions could not be listed.')}</p>
              ) : versions.map((v) => (
                <div key={v.id} className="fact">
                  <span className="fk"><Fig>v{v.version}</Fig></span>
                  <span>
                    {v.title ?? t('Untitled dossier')} <Ref>{v.ref}</Ref>
                    {v.id === dossier.id
                      ? <> <span className="d">· {t('shown')}</span></>
                      : <> · <Lnk onClick={() => { void view(v.id); }}>{t('Show')}</Lnk></>}
                  </span>
                  <span className="fmeta"><Fig>{fmtDate(v.created_at)}</Fig> <Ref>{v.run_ref ?? v.principal ?? ''}</Ref></span>
                </div>
              ))}
            </div>
          )}
        </Sheet>
      )}
    </Zone>
  );
}
